// Service subpages. The first ~14 have full long-form copy (~2,000+ chars).
// All other slugs fall back to a strong templated layout using these fields.

export const SERVICE_CATEGORIES = [
  {
    title: "Roofing",
    services: [
      "roof-repair",
      "roof-replacement",
      "storm-damage-roofing",
      "storm-damage-repairs",
      "gutter-drainage-repairs",
      "fascia-soffit-repair",
    ],
  },
  {
    title: "Remodeling",
    services: [
      "kitchen-remodeling",
      "bathroom-remodeling",
      "full-home-remodeling",
      "interior-feature-upgrades",
      "flooring-installation",
      "interior-painting",
      "trim-carpentry",
    ],
  },
  {
    title: "Additions & Outdoor",
    services: [
      "home-additions",
      "adu-construction",
      "garage-conversions",
      "outdoor-living",
      "deck-building",
      "patio-covers",
      "pergolas",
    ],
  },
  {
    title: "Repairs & Exterior",
    services: [
      "exterior-repairs",
      "siding-trim-repairs",
      "drywall-painting",
      "fence-gate-work",
      "window-door-installation",
      "carpentry-framing",
      "exterior-rot-repair",
      "exterior-painting",
      "water-damage-repairs",
      "door-repair",
      "deck-repair",
      "patio-repair",
      "fence-repair",
    ],
  },
  {
    title: "Maintenance & Investor",
    services: [
      "home-maintenance-plans",
      "handyman-punch-list",
      "investor-property-repairs",
      "make-ready-repairs",
      "rental-property-maintenance",
      "turnover-repairs",
    ],
  },
];

export const SERVICES = {
  "kitchen-remodeling": {
    title: "Kitchen Remodeling in Austin, TX",
    short: "Kitchen Remodeling",
    metaTitle: "Kitchen Remodeling Austin TX | Black Ridge Construction",
    metaDescription:
      "Practical, durable kitchen remodels in Austin and Central Texas. Layout updates, cabinets, counters, lighting, and finishes coordinated by one general contractor.",
    hero: "Practical, Durable Kitchen Remodels for Austin Homes",
    intro:
      "Black Ridge Construction handles kitchen remodels across Austin and the surrounding Central Texas communities. Whether you want to refresh finishes, change the layout, or take a tired kitchen down to studs, our team coordinates the entire scope under one general contractor so you only manage one relationship instead of five.",
    overview: [
      "Kitchens are the most-used room in most Austin homes, and they also tend to age faster than the rest of the house. Cabinets sag, hinges loosen, lighting falls behind modern needs, and old layouts stop working for how your family actually uses the space. Black Ridge Construction approaches every kitchen remodel as a long-term, real-life upgrade — not a Pinterest project that ignores how the kitchen has to function on a Tuesday night.",
      "We can lead a light cosmetic refresh, a mid-range remodel with new cabinets and counters, or a full layout change involving plumbing relocation, electrical updates, structural openings, and new lighting plans. Because we manage roofing, exterior, remodeling, and repair work, we naturally catch issues other remodelers ignore — like water-stained subfloor at a sink, hidden rot behind a window above a counter, or settling drywall around an opening you want to widen.",
      "Our process is direct and practical. We walk the kitchen, talk through how you live in it, give you honest options at multiple price levels, and then build a clear scope so you actually know what you're paying for. We handle subcontractor coordination for plumbing, electrical, tile, counters, and flooring trades so your project stays moving instead of stalling between phases.",
    ],
    problemsSolved: [
      "Outdated, broken, or sagging cabinets",
      "Bad layout that wastes counter space",
      "Old fluorescent or under-powered lighting",
      "Limited storage and pantry capacity",
      "Damaged or dated countertops, sinks, and faucets",
      "Subfloor damage under leaky sinks or dishwashers",
      "Walls or openings that block the way you actually cook",
    ],
    process: true,
    relatedServices: [
      "bathroom-remodeling",
      "full-home-remodeling",
      "flooring-installation",
      "home-additions",
    ],
    faqs: [
      {
        q: "How long does a kitchen remodel take in Austin?",
        a: "Most full kitchen remodels run 6-10 weeks once materials are on site. Cosmetic refreshes can run 2-4 weeks. Timeline depends on cabinet lead times, scope of plumbing or electrical work, and how custom the finish package is.",
      },
      {
        q: "Can you change my kitchen layout?",
        a: "Yes. We regularly remove non-structural walls, relocate plumbing, and re-plan electrical so the kitchen actually works for how you live. Anything load-bearing is engineered properly before demo.",
      },
      {
        q: "Do you handle cabinets, counters, plumbing, and electrical?",
        a: "Yes. We coordinate every trade under one general contractor, so you have one point of contact instead of five.",
      },
      {
        q: "Can my maintenance plan help with future kitchen issues?",
        a: "Yes. HomeCare Plus and Reserve memberships include minor punch work, caulking, hardware adjustments, and member pricing on approved repairs — useful for the small things that come up after a remodel.",
      },
    ],
  },
  "bathroom-remodeling": {
    title: "Bathroom Remodeling in Austin, TX",
    short: "Bathroom Remodeling",
    metaTitle: "Bathroom Remodeling Austin TX | Black Ridge Construction",
    metaDescription:
      "Bathroom remodels and upgrades in Austin and Central Texas. Tile, plumbing, layout updates, walk-in showers, and aging-in-place modifications handled by one GC.",
    hero: "Bathroom Remodels Built for Real Austin Homes",
    intro:
      "From small bathroom refreshes to full tear-outs with new plumbing, tile, glass, and fixtures, Black Ridge Construction handles bathroom remodels across Austin and Central Texas. Our focus is on durability, water-tight installations, and finishes you'll still like in five years.",
    overview: [
      "Bathrooms are where bad construction shows up fastest. Improper tile work leaks, weak plumbing connections drip, cheap vanities warp from humidity, and bad ventilation creates mold problems behind walls. Black Ridge Construction approaches bathroom remodels with these realities in mind — we are not just picking finishes, we are rebuilding a wet room.",
      "We handle full guest bath updates, primary bath remodels, walk-in shower conversions, tile-to-ceiling installs, plumbing fixture upgrades, electrical safety updates, ventilation improvements, niche and bench installations, double-vanity additions, and aging-in-place modifications such as zero-threshold showers and grab-bar reinforcement. Many of our Austin clients are also adding heated floors and upgraded lighting to their primary bath.",
      "As a general contractor, we coordinate plumbing, tile, electrical, glass, paint, and finish carpentry — so the project stays on schedule and the work is done in the right order. Because we also do roofing and exterior work, we naturally catch issues other remodelers miss, like roof venting tied to exhaust fans, exterior wall flashing, or water staining traveling from above.",
    ],
    problemsSolved: [
      "Cracked tile, failing grout, or leaking pans",
      "Outdated vanities and dim lighting",
      "Bad ventilation causing mold and moisture damage",
      "Cramped layouts in older Central Texas homes",
      "Step-over tubs that are no longer safe",
      "Plumbing and electrical that hasn't been updated in decades",
    ],
    process: true,
    relatedServices: [
      "kitchen-remodeling",
      "full-home-remodeling",
      "flooring-installation",
      "water-damage-repairs",
    ],
    faqs: [
      {
        q: "How long does a bathroom remodel take?",
        a: "A typical full bathroom remodel takes 3-6 weeks. Smaller refreshes can run 1-2 weeks. Custom tile work, glass lead times, and plumbing relocations are the most common timeline drivers.",
      },
      {
        q: "Can you convert a tub to a walk-in shower?",
        a: "Yes. Tub-to-shower conversions are one of our most common requests for Austin homeowners and aging-in-place clients.",
      },
      {
        q: "Do you handle tile, plumbing, and glass?",
        a: "Yes. We coordinate every trade in-house or through our trusted subcontractor network so you only have one point of contact.",
      },
    ],
  },
  "full-home-remodeling": {
    title: "Full Home Remodeling in Austin, TX",
    short: "Full Home Remodeling",
    metaTitle: "Full Home Remodel Austin TX | Black Ridge Construction",
    metaDescription:
      "Whole-house remodels in Austin and Central Texas. Layout changes, finishes, kitchens, baths, flooring, exterior, and roofing — managed by one trusted general contractor.",
    hero: "Whole-House Remodels Managed Under One Roof",
    intro:
      "Black Ridge Construction takes on full home remodels across Austin and Central Texas. When the scope crosses multiple rooms, trades, and exterior systems, you want a real general contractor — not a single-trade company stretching outside its lane.",
    overview: [
      "A full home remodel is the most complex residential project Black Ridge handles, and it is where the value of a full-service GC really shows. You are coordinating demo, framing, plumbing, electrical, HVAC, drywall, paint, flooring, tile, cabinets, counters, finish carpentry, lighting, fixtures, exterior repair, roofing touch-ups, and final punch — often all in motion at once. We sequence these phases so trades do not block each other, finishes do not get damaged, and the home is buttoned up properly at the end.",
      "Common scopes include: opening up dated layouts; gutting kitchens and baths; replacing flooring and trim; updating lighting and electrical; refreshing or replacing windows and doors; new paint inside and out; refreshing or replacing roofing and exterior; and addressing deferred maintenance that has been ignored for years. Because Black Ridge handles roofing, exterior, interior, and ongoing maintenance, we frequently catch issues other remodelers walk past.",
      "For investor and resale-focused remodels, we focus on the upgrades that actually move value: kitchens, baths, flooring, paint, lighting, exterior cleanup, and roof condition. For long-term homeowners, we focus on how the home will function for the next 10-20 years — better storage, better light, better flow, fewer maintenance headaches.",
    ],
    problemsSolved: [
      "Outdated floor plans and choppy layouts",
      "Multiple rooms aging at the same time",
      "Stacked deferred maintenance issues",
      "Mismatched finishes from years of patchwork work",
      "Investor properties needing a full reset before resale or rental",
    ],
    process: true,
    relatedServices: [
      "kitchen-remodeling",
      "bathroom-remodeling",
      "home-additions",
      "roof-replacement",
    ],
    faqs: [
      {
        q: "How long does a full home remodel take?",
        a: "Most full home remodels run 3-9 months depending on scope, square footage, and how much of the home is being touched. Layout changes, additions, and structural work add time. We give realistic timelines, not optimistic ones.",
      },
      {
        q: "Do you handle plans and permits?",
        a: "Yes. For projects that require permits and engineering, we coordinate with designers, engineers, and the city as part of our process.",
      },
    ],
  },
  "interior-feature-upgrades": {
    title: "Interior Feature Upgrades in Austin, TX",
    short: "Interior Feature Upgrades",
    metaTitle: "Interior Feature Upgrades Austin TX | Black Ridge Construction",
    metaDescription:
      "Black Ridge Construction builds accent walls, electric fireplace feature walls, TV centers, media walls, built-ins, and custom interior upgrades for Austin-area homes.",
    hero: "Interior Feature Upgrades in Austin, TX",
    intro:
      "Black Ridge Construction helps Austin-area homeowners add custom interior features that make a room feel more finished, modern, and valuable without requiring a full remodel. Interior feature upgrades can include accent walls, electric fireplace feature walls, TV centers, media walls, built-ins, shelving, trim details, and living room focal-point upgrades.",
    overview: [
      "These projects are a good fit for homeowners who want to improve the look of a living room, bedroom, office, entryway, or entertainment space with a focused construction project instead of a larger renovation. Rather than tearing the room apart, we build one or two high-impact features that change how the whole space feels and functions.",
      "Common scopes include floor-to-ceiling accent walls in wood, shiplap, or board-and-batten; electric fireplace feature walls with integrated TV niches and stone or tile surrounds; full media walls with concealed wiring and storage; symmetrical built-ins flanking a fireplace or window; custom shelving installations; and decorative trim, paneling, and wainscoting that gives a wall real depth and craftsmanship. Each project is built to look intentional with the rest of the home, not bolted on.",
      "Depending on the scope, Black Ridge Construction can coordinate framing, drywall, trim, paneling, shelving, paint, finish details, and project management. Electrical connections, outlet relocation, dedicated circuits, TV mounting, or code-required electrical work may be handled by a licensed electrical trade partner when required. We sequence the trades so the finished feature looks crisp at every detail.",
    ],
    problemsSolved: [
      "Plain living room walls with no visual interest",
      "Outdated brick or builder-grade fireplaces that drag the room down",
      "Messy exposed wiring around mounted TVs",
      "Awkward unused wall space crying out for built-ins",
      "Open-shelving and storage that doesn't match the rest of the home",
      "Office, bedroom, or entry walls that feel unfinished",
    ],
    process: true,
    relatedServices: [
      "trim-carpentry",
      "interior-painting",
      "drywall-painting",
      "full-home-remodeling",
    ],
    faqs: [
      {
        q: "What are interior feature upgrades?",
        a: "Interior feature upgrades are focused projects that improve the look and function of a room without requiring a full remodel. Examples include accent walls, electric fireplace feature walls, TV centers, media walls, built-ins, and custom trim details.",
      },
      {
        q: "Can Black Ridge Construction build a TV wall with an electric fireplace?",
        a: "Yes. Black Ridge Construction can coordinate the build-out, finish carpentry, trim, drywall, paint, and related construction details. Electrical work may require a licensed electrical trade partner depending on the scope.",
      },
      {
        q: "Are these standalone projects or part of a remodel?",
        a: "They can be either. Interior feature upgrades can be completed as standalone projects or included as part of a larger living room, bedroom, office, or full-home remodel.",
      },
    ],
  },
  "roof-repair": {
    title: "Roof Repair in Austin, TX",
    short: "Roof Repair",
    metaTitle: "Roof Repair Austin TX | Black Ridge Construction",
    metaDescription:
      "Honest roof repair in Austin and Central Texas. Leak detection, shingle repair, flashing, boots, valleys, and storm follow-up handled by a full-service contractor.",
    hero: "Honest Roof Repair for Austin & Central Texas Homes",
    intro:
      "Black Ridge Construction handles roof repairs across Austin and the surrounding Central Texas communities. Whether you have a small leak, a damaged shingle field after a storm, failing flashing around a chimney or skylight, or a pipe boot that has dried out and cracked, our team can inspect, document, and quote real fixes that solve the underlying problem.",
    overview: [
      "Most roof leaks in Central Texas don't come from where homeowners think they do. Water travels along framing and decking before it ever shows on a ceiling, so a stain in one room can come from a flashing failure 10 feet away. We inspect the actual roof surface, photograph problem areas, and walk through what we see before we quote — so you know what's being fixed and why.",
      "Common roof repair scopes include shingle field repairs, ridge cap replacement, valley sealing, pipe boot replacement, chimney and skylight flashing repair, drip edge correction, exposed-nail repair, vent and accessory resealing, gutter-related water intrusion, and storm follow-up tarping or temporary protection.",
      "We do not pressure homeowners into replacements when a repair will solve the problem. We also do not promise insurance approvals — we document what we see, write clear estimates, and let homeowners decide how to proceed.",
    ],
    problemsSolved: [
      "Active leaks during storms",
      "Failed pipe boots and dried-out flashing",
      "Missing or wind-damaged shingles",
      "Hail bruising and granule loss",
      "Improper or aged ridge cap and valley work",
      "Water intrusion from poor gutter or drip-edge detail",
    ],
    process: true,
    relatedServices: [
      "roof-replacement",
      "storm-damage-roofing",
      "gutter-drainage-repairs",
      "home-maintenance-plans",
    ],
    faqs: [
      {
        q: "Can you inspect my roof in Austin?",
        a: "Yes. We schedule visual roof inspections for Austin and Central Texas homeowners. We document what we see and walk through findings with you.",
      },
      {
        q: "Do you help with storm damage?",
        a: "Yes. We document storm-related damage, can install temporary protection where appropriate, and coordinate roof repairs. We do not promise insurance claim approval.",
      },
      {
        q: "Is roof repair always better than replacement?",
        a: "Not always. If a roof is past its useful life, a repair can be a short-term fix at best. We give honest options based on what we see.",
      },
    ],
  },
  "roof-replacement": {
    title: "Roof Replacement in Austin, TX",
    short: "Roof Replacement",
    metaTitle: "Roof Replacement Austin TX | Black Ridge Construction",
    metaDescription:
      "Full roof replacement in Austin and Central Texas. Shingle and metal coordination, decking, underlayment, ventilation, and clean job sites.",
    hero: "Roof Replacements Done Right, the First Time",
    intro:
      "When repair is no longer enough, Black Ridge Construction handles full roof replacements for Austin and Central Texas homes. We inspect decking, install proper underlayment, address ventilation and flashing, and clean the job site every day.",
    overview: [
      "A roof replacement is the most important exterior project a home will ever go through. It protects every other system — drywall, framing, insulation, electrical, HVAC ducting, and finished interiors. Cutting corners on a roof replacement creates years of expensive interior damage.",
      "Black Ridge replaces shingle roofs and coordinates metal roofing through trusted partners. Each replacement includes a decking inspection, replacement of damaged sheathing where found, drip edge correction, ice/water shield in the right areas, proper underlayment, new flashing details, ventilation review, and final cleanup with magnetic nail sweeps.",
      "We do not promise insurance claim approvals. We do document what we see, write clear estimates, and explain options. We respect the property — daily cleanup, careful staging of materials, and protection of landscape and AC units.",
    ],
    problemsSolved: [
      "Roof past its useful life",
      "Repeated leaks after multiple repairs",
      "Storm-damaged shingles, decking, or flashing",
      "Improper ventilation creating heat and moisture issues",
      "Roofs nearing resale where buyers will demand a replacement",
    ],
    process: true,
    relatedServices: [
      "roof-repair",
      "storm-damage-roofing",
      "gutter-drainage-repairs",
      "fascia-soffit-repair",
    ],
    faqs: [
      {
        q: "How long does a roof replacement take?",
        a: "Most residential roof replacements complete in 1-3 days. Larger or steeper roofs and metal coordination can extend timelines.",
      },
      {
        q: "Do you replace decking when it's damaged?",
        a: "Yes. We inspect decking once the old roof is off and replace damaged sheathing as needed. We document and discuss any major findings.",
      },
    ],
  },
  "storm-damage-roofing": {
    title: "Storm Damage Roofing in Austin, TX",
    short: "Storm Damage Roofing",
    metaTitle: "Storm Damage Roofing Austin TX | Black Ridge Construction",
    metaDescription:
      "After-storm roof inspections, temporary protection, and repair coordination across Austin and Central Texas. Honest documentation — no insurance claim guarantees.",
    hero: "After-Storm Roof Help for Central Texas Homes",
    intro:
      "Central Texas storms move fast and hit hard. Hail, wind, and sudden downpours can damage shingles, flashing, and ventilation accessories in minutes. Black Ridge Construction inspects storm-affected roofs across Austin and Central Texas, documents what we see, can install temporary protection where appropriate, and coordinates repair or replacement work.",
    overview: [
      "After a storm, we recommend not climbing on the roof yourself. We schedule a visual inspection, photograph problem areas, and walk through findings. If active leaking is occurring, we can install temporary tarping or sealing where conditions allow.",
      "We do not promise insurance approvals — we document, write clear estimates, and let homeowners decide how to proceed. If you are working with insurance, we will share our findings and photos. If you are paying out of pocket, we will share honest options at different price points.",
    ],
    problemsSolved: [
      "Sudden roof leaks after hail or wind",
      "Visibly missing or lifted shingles",
      "Tree limb damage to roofing or fascia",
      "Hail bruising and granule loss",
      "Damaged ridge caps, ventilation, or flashing",
    ],
    process: true,
    relatedServices: [
      "roof-repair",
      "roof-replacement",
      "water-damage-repairs",
      "fascia-soffit-repair",
    ],
    faqs: [
      {
        q: "Do you guarantee my insurance claim will be approved?",
        a: "No. We document what we see honestly and provide clear estimates. We do not promise insurance approvals, and we do not pressure homeowners into work.",
      },
    ],
  },
  "home-additions": {
    title: "Home Additions in Austin, TX",
    short: "Home Additions",
    metaTitle: "Home Additions Austin TX | Black Ridge Construction",
    metaDescription:
      "Room additions, second story additions, and bump-outs across Austin and Central Texas. Foundation, framing, roofing, electrical, and finish work under one GC.",
    hero: "Add the Space Your Home Needs",
    intro:
      "Black Ridge Construction handles room additions, bump-outs, master suite additions, and small second-floor additions for Austin and Central Texas homeowners. Additions touch nearly every system in the home, so they belong with a real general contractor — not a single-trade installer.",
    overview: [
      "Common Austin additions include a new master suite, an expanded family room, a home office or studio addition, a sunroom or covered enclosure, a bump-out at the kitchen or bath, and small second-story additions over existing footprint. Each one involves design, permits, engineering, foundation, framing, roofing tie-in, exterior siding tie-in, electrical, HVAC extension, drywall, paint, flooring, and finish carpentry.",
      "Black Ridge coordinates the whole scope from design through final punch. We work with engineers, designers, and trusted subcontractor partners so the project moves on schedule. Because we also do roofing and exterior work, the tie-ins between the old and new roof, siding, and trim are detailed properly — which is one of the most common places we see other contractors cut corners.",
    ],
    problemsSolved: [
      "Outgrowing your current home but not wanting to move",
      "Need for a private office or studio space",
      "Lack of master suite or proper second bathroom",
      "Cramped living, dining, or kitchen footprint",
      "Aging family members moving in and needing dedicated space",
    ],
    process: true,
    relatedServices: [
      "adu-construction",
      "garage-conversions",
      "full-home-remodeling",
      "outdoor-living",
    ],
    faqs: [
      {
        q: "Do you handle design and engineering for additions?",
        a: "Yes. We coordinate design, structural engineering, and city permitting as part of the project scope.",
      },
    ],
  },
  "adu-construction": {
    title: "ADU Construction in Austin, TX",
    short: "ADU Construction",
    metaTitle: "ADU Construction Austin TX | Black Ridge Construction",
    metaDescription:
      "ADUs, guest suites, and detached living spaces in Austin and Central Texas. Foundation, framing, plumbing, electrical, HVAC, and finishes under one general contractor.",
    hero: "ADUs & Guest Suites for Austin Homeowners",
    intro:
      "Black Ridge Construction builds accessory dwelling units (ADUs), backyard guest suites, and detached studios for Austin and Central Texas homeowners. ADUs add rental income potential, dedicated guest or family living space, and long-term value to your property.",
    overview: [
      "An ADU is essentially a small, self-contained home. It typically includes foundation, framing, roofing, exterior, plumbing, electrical, HVAC, kitchenette or full kitchen, bathroom, finishes, and trim. Because every system in the home is involved, ADUs benefit enormously from a full-service general contractor coordinating the whole scope.",
      "Austin's ADU rules vary by lot and zoning, and we coordinate with designers and engineers to make sure the scope fits what your property allows. Common uses we see in Austin include long-term rental income, short-term rental income, multi-generational living for aging parents, dedicated home office or studio space, and flexible adult-child living.",
    ],
    problemsSolved: [
      "Need for rental income on your existing lot",
      "Aging parents or returning adult children needing space",
      "Lack of private office, studio, or guest space",
      "Desire to add value before selling without moving",
    ],
    process: true,
    relatedServices: [
      "home-additions",
      "garage-conversions",
      "full-home-remodeling",
      "outdoor-living",
    ],
    faqs: [
      {
        q: "Are ADUs allowed in Austin?",
        a: "Many lots in Austin allow ADUs under certain conditions. Rules vary by zoning, lot size, and setbacks. We work with designers and engineers to confirm your lot's options.",
      },
    ],
  },
  "outdoor-living": {
    title: "Outdoor Living in Austin, TX",
    short: "Outdoor Living",
    metaTitle: "Outdoor Living Austin TX | Black Ridge Construction",
    metaDescription:
      "Covered patios, pergolas, decks, outdoor kitchens, and shaded outdoor living spaces designed for Central Texas heat, sun, and storms.",
    hero: "Outdoor Living Built for Central Texas Weather",
    intro:
      "Black Ridge Construction designs and builds outdoor living spaces that hold up to Texas heat, sun, wind, and storms. Whether you want a shaded patio cover, a pergola, a hardwood or composite deck, an outdoor kitchen, or a full outdoor living room, we coordinate the entire build.",
    overview: [
      "Austin homeowners use their yards more than most parts of the country. The right outdoor space adds livable square footage without the permit complexity of an interior addition. Black Ridge handles covered patios, pergolas, decks, paver patios, outdoor kitchens, outdoor fireplaces, and shaded living areas for grilling, dining, and entertaining.",
      "Because we build with Texas weather in mind, we use materials and detailing that hold up to UV, wind, and storms. We coordinate concrete, framing, roofing, electrical (for fans, lighting, and outlets), plumbing where needed for kitchens and sinks, and finish work.",
    ],
    problemsSolved: [
      "Backyards too hot to use most of the year",
      "Need for shaded outdoor entertaining space",
      "Want to grill, cook, and entertain outside",
      "Aging deck or patio that no longer feels safe or attractive",
      "Adding livable space without the cost of a full addition",
    ],
    process: true,
    relatedServices: [
      "deck-building",
      "patio-covers",
      "pergolas",
      "home-additions",
    ],
    faqs: [
      {
        q: "Can you build a covered patio that works with my roofline?",
        a: "Yes. We tie new covered patios into existing roofing carefully so water sheds correctly and the new structure looks original to the home.",
      },
    ],
  },
  "home-maintenance-plans": {
    title: "Home Maintenance Plans in Austin, TX",
    short: "Maintenance Plans",
    metaTitle: "Home Maintenance Plans Austin TX | Black Ridge Construction",
    metaDescription:
      "Monthly home maintenance memberships for Austin and Central Texas homeowners. Inspections, priority scheduling, and defined repair reserve allowances.",
    hero: "Maintenance Memberships for Austin Homeowners",
    intro:
      "Black Ridge Construction offers monthly maintenance memberships designed to help Austin and Central Texas homeowners stay ahead of small issues, get priority scheduling, and keep a trusted contractor familiar with the property. Plans are maintenance memberships and repair reserve agreements — not insurance, not warranties, and not unlimited-repair programs.",
    overview: [
      "Each plan includes planned maintenance visits, a defined annual covered-work allowance, a per-claim cap, and member benefits like priority scheduling and discounted approved work. Larger or older homes typically benefit from Plus or Reserve plans.",
    ],
    problemsSolved: [
      "Small issues that get ignored until they become expensive",
      "Not knowing which contractor to call for everyday issues",
      "Rental property and investor portfolios needing recurring attention",
      "Older homes that need consistent oversight",
    ],
    process: true,
    relatedServices: [
      "handyman-punch-list",
      "investor-property-repairs",
      "roof-repair",
      "exterior-repairs",
    ],
    faqs: [
      {
        q: "Is the maintenance plan insurance?",
        a: "No. It is a maintenance membership with a defined repair reserve. It is not insurance, warranty, or unlimited repair coverage.",
      },
      {
        q: "What is included?",
        a: "Planned visits, defined covered-work allowance, per-claim cap, priority scheduling, and member pricing on approved repairs. See the Maintenance Plans page for full details.",
      },
    ],
  },
  "investor-property-repairs": {
    title: "Investor & Rental Property Repairs in Austin, TX",
    short: "Investor Property Repairs",
    metaTitle: "Investor Property Repairs Austin TX | Black Ridge Construction",
    metaDescription:
      "Make-ready, turnover, and repair coordination for Austin investors and property managers. One general contractor for multiple properties and trades.",
    hero: "Repairs & Make-Readies for Austin Investors",
    intro:
      "Black Ridge Construction works with Austin investors and property managers on make-ready repairs, turnover scopes, deferred maintenance lists, and recurring portfolio care. One general contractor across multiple trades means fewer scheduling headaches and fewer surprises.",
    overview: [
      "Investor work has its own pace and pressure. Properties have to be turned quickly between tenants, deferred maintenance has to be addressed without overbuilding, and pricing has to be realistic to protect cash flow. Black Ridge approaches investor projects with that mindset — practical scopes, clear pricing, and reliable execution.",
      "Our PropertyCare and Portfolio plans are designed for investors and managers who need recurring monthly support across one or many properties.",
    ],
    problemsSolved: [
      "Turnover and make-ready repairs between tenants",
      "Deferred maintenance on long-held rentals",
      "Repairs and refresh work before resale",
      "Recurring portfolio maintenance and reporting",
    ],
    process: true,
    relatedServices: [
      "make-ready-repairs",
      "rental-property-maintenance",
      "turnover-repairs",
      "handyman-punch-list",
    ],
    faqs: [
      {
        q: "Do you handle multiple properties at once?",
        a: "Yes. Our PropertyCare Pro and Portfolio Reserve plans are designed for managers and investors with multiple properties.",
      },
    ],
  },
  "handyman-punch-list": {
    title: "Handyman & Punch List Repairs in Austin, TX",
    short: "Handyman & Punch List",
    metaTitle: "Handyman & Punch List Repairs Austin TX | Black Ridge Construction",
    metaDescription:
      "Punch list repairs, small projects, and one-off handyman scopes across Austin and Central Texas — handled by a real general contractor crew.",
    hero: "Punch List & Small-Project Help",
    intro:
      "Black Ridge Construction handles small repairs, punch list items, and one-off handyman scopes for Austin and Central Texas homeowners. Not every job needs a full remodel — sometimes you just need a real contractor to handle a list of small things correctly.",
    overview: [
      "Typical punch list scopes include drywall patching, paint touch-ups, door and hardware adjustments, minor trim work, caulking, light fixture replacement, fence and gate fixes, fascia repairs, and the dozens of small things that accumulate over time.",
      "Maintenance members get priority scheduling and member pricing on approved punch work. For homeowners outside a plan, we schedule punch work as availability allows.",
    ],
    problemsSolved: [
      "Lists of small things you've been meaning to fix",
      "Pre-sale punch lists before going to market",
      "After-move-in adjustments in new homes",
      "Repairs that don't fit one trade",
    ],
    process: true,
    relatedServices: [
      "home-maintenance-plans",
      "investor-property-repairs",
      "drywall-painting",
      "trim-carpentry",
    ],
    faqs: [
      {
        q: "Is there a minimum project size?",
        a: "We have a practical minimum visit charge to make small jobs work. Maintenance members get member pricing and may have dispatch credits available.",
      },
    ],
  },
  "deck-building": {
    title: "Deck Building in Austin, TX",
    short: "Deck Building",
    metaTitle: "Deck Builders Austin TX | Black Ridge Construction",
    metaDescription:
      "Custom wood and composite decks built for Central Texas weather. Framing, finishes, railings, and stairs by one trusted general contractor.",
    hero: "Decks Built for Central Texas Weather",
    intro:
      "Black Ridge Construction designs and builds wood and composite decks for Austin and Central Texas homes. We focus on framing that lasts, fasteners that hold, and finishes that handle the sun and rain cycles unique to Texas.",
    overview: [
      "Most deck failures in Central Texas come from undersized framing, the wrong fasteners, or fast-fade finishes. We build with proper joist spacing, corrosion-resistant fasteners, and material choices that fit your budget and how the deck will actually be used. Composite decks reduce maintenance; quality cedar or hardwood decks bring a more natural look at a different price point.",
    ],
    problemsSolved: [
      "Old, splintering, or unsafe deck surfaces",
      "Sagging or undersized framing",
      "Decks that need to tie into a new patio cover or pergola",
      "Adding usable backyard space without an addition",
    ],
    process: true,
    relatedServices: [
      "deck-repair",
      "patio-covers",
      "pergolas",
      "outdoor-living",
    ],
    faqs: [],
  },
};

// All service slugs (full set) for routing + sitemap.
export const ALL_SERVICE_SLUGS = [
  "kitchen-remodeling",
  "bathroom-remodeling",
  "full-home-remodeling",
  "interior-feature-upgrades",
  "roof-repair",
  "roof-replacement",
  "storm-damage-roofing",
  "home-additions",
  "adu-construction",
  "garage-conversions",
  "outdoor-living",
  "deck-building",
  "patio-covers",
  "pergolas",
  "exterior-repairs",
  "siding-trim-repairs",
  "drywall-painting",
  "flooring-installation",
  "fence-gate-work",
  "home-maintenance-plans",
  "handyman-punch-list",
  "investor-property-repairs",
  "make-ready-repairs",
  "water-damage-repairs",
  "window-door-installation",
  "carpentry-framing",
  "gutter-drainage-repairs",
  "deck-repair",
  "patio-repair",
  "fence-repair",
  "door-repair",
  "trim-carpentry",
  "interior-painting",
  "exterior-painting",
  "rental-property-maintenance",
  "turnover-repairs",
  "storm-damage-repairs",
  "exterior-rot-repair",
  "fascia-soffit-repair",
];

export function slugToTitle(slug) {
  return slug
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}
